/**
 * Product pictures: upload to Supabase bucket then create/delete via backend API.
 * Ensures bucket and DB stay in sync (upload bucket first then create; delete bucket first then delete record).
 */
import axios from 'axios';
import { authHeaders } from './apiAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// Prefer REACT_APP_ so CRA exposes it; fall back to SUPABASE_URL if your build injects it (e.g. from root .env)
const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL || process.env.SUPABASE_URL || '').replace(/\/$/, '');
const BUCKET = 'product_pictures';

/**
 * Upload a file to the product_pictures bucket (Supabase Storage).
 * @param {string} accessToken - User's JWT (used for storage auth)
 * @param {string} path - Storage path e.g. "123/uuid_filename.jpg"
 * @param {File} file - The image file
 * @returns {Promise<{ path: string }>} Storage path on success
 */
async function uploadToBucket(accessToken, path, file) {
  if (!SUPABASE_URL) {
    throw new Error('REACT_APP_SUPABASE_URL is not set');
  }
  const url = `${SUPABASE_URL}/storage/v1/object/${BUCKET}/${path}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': file.type || 'application/octet-stream'
    },
    body: file
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Storage upload failed: ${response.status}`);
  }
  const data = await response.json().catch(() => ({}));
  return { path: data.Key || path };
}

/**
 * Get public URL for a path in the product_pictures bucket.
 * @param {string} path - Storage path
 * @returns {string}
 */
export function getPicturePublicUrl(path) {
  if (!SUPABASE_URL || !path) return '';
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${path}`;
}

/**
 * Delete a file from the product_pictures bucket.
 * @param {string} accessToken - User's JWT
 * @param {string} path - Storage path (e.g. from product_picture.picture_path)
 */
async function deleteFromBucket(accessToken, path) {
  if (!SUPABASE_URL) {
    throw new Error('REACT_APP_SUPABASE_URL is not set');
  }
  if (!path) return;
  const encodedPath = path.split('/').map(segment => encodeURIComponent(segment)).join('/');
  const url = `${SUPABASE_URL}/storage/v1/object/${BUCKET}/${encodedPath}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Storage delete failed: ${response.status}`);
  }
}

/**
 * Sanitize file name for storage path (avoid path traversal and keep extension).
 * @param {string} name
 * @returns {string}
 */
function sanitizeFileName(name) {
  const base = (name || 'image').replace(/[^a-zA-Z0-9._-]/g, '_');
  return base.length > 100 ? base.slice(0, 100) : base;
}

/**
 * Upload a picture for a product: save to bucket first, then create record via backend.
 * @param {string} accessToken - User's access token
 * @param {number} productId - Product id
 * @param {File} file - Image file
 * @returns {Promise<{ success: boolean, product_picture?: object }>}
 */
export const uploadProductPicture = async (accessToken, productId, file) => {
  if (!file || !productId) {
    throw new Error('Product id and file are required');
  }
  const path = `${productId}/${crypto.randomUUID()}_${sanitizeFileName(file.name)}`;
  await uploadToBucket(accessToken, path, file);
  const picture_url = getPicturePublicUrl(path);
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/product-pictures`,
      {
        product_picture: {
          product_id: productId,
          picture_path: path,
          picture_file_name: file.name || null,
          picture_url
        }
      },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to create product picture record');
  }
};

/**
 * Get all pictures for a product (from backend; includes picture_url for display).
 * @param {string} accessToken - User's access token
 * @param {number} productId - Product id
 * @returns {Promise<{ success: boolean, product_pictures: Array }>}
 */
export const getProductPictures = async (accessToken, productId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/product-pictures`, {
      params: { product_id: productId },
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get product pictures');
  }
};

/**
 * Get one product picture by id (from backend).
 * @param {string} accessToken - User's access token
 * @param {number} pictureId - Picture id
 * @returns {Promise<{ success: boolean, product_picture?: object }>}
 */
export const getProductPicture = async (accessToken, pictureId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/product-pictures/${pictureId}`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get product picture');
  }
};

/**
 * Delete a product picture: remove from bucket first, then delete record via backend.
 * Fetches the picture to get picture_path, then deletes from storage, then calls backend.
 * @param {string} accessToken - User's access token
 * @param {number} pictureId - Picture id
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export const deleteProductPicture = async (accessToken, pictureId) => {
  const { success, product_picture } = await getProductPicture(accessToken, pictureId);
  if (!success || !product_picture) {
    throw new Error('Product picture not found');
  }
  const path = product_picture.picture_path;
  if (path) {
    await deleteFromBucket(accessToken, path);
  }
  try {
    const response = await axios.delete(`${API_BASE_URL}/api/product-pictures/${pictureId}`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete product picture record');
  }
};

/**
 * Delete all picture records for a product (backend only).
 * Does not remove files from storage; use when you need to clear records only
 * or after deleting files separately. For full cleanup, delete each picture
 * with deleteProductPicture so bucket and DB stay in sync.
 * @param {string} accessToken - User's access token
 * @param {number} productId - Product id
 * @returns {Promise<{ success: boolean, message?: string, deleted_count?: number }>}
 */
export const deleteAllProductPictures = async (accessToken, productId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/api/product-pictures`, {
      params: { product_id: productId },
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete product pictures');
  }
};
